/**
 * ==========================================
 * صفحة تخزين أكواد الكوبونات في الفواتير
 * ==========================================
 * 
 * هذه الصفحة تعرض جميع سجلات تخزين الكوبونات المطبقة على الفواتير
 * وتتيح للمستخدمين البحث والإحصاء وإدارة الكوبونات
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Alert,
  CircularProgress,
  Divider,
  Avatar,
  Tooltip,
  Pagination,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  alpha,
  useTheme
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  BarChart as BarChartIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  LocalOffer as CouponIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  History as HistoryIcon,
  Store as StoreIcon,
  Event as CalendarIcon,
  AttachMoney as MoneyIcon,
  Percent as PercentIcon,
  Tag as TagIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Group as AccountGroupIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { format } from 'date-fns';
import { arSA } from 'date-fns/locale';

// استيراد الخدمات
import * as couponStorageService from '../services/couponStorageService';

// استيراد الأنواع
import { 
  InvoiceCouponStorage,
  CouponStorageSearchCriteria,
  CouponUsageStatistics
} from '../types/couponStorage';

// أنماط مخصصة
const StyledCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.light, 0.1)} 100%)`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  }
}));

const StatCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[3],
  }
}));

const CouponStoragePage: React.FC = () => {
  const theme = useTheme();
  
  // الحالات
  const [storages, setStorages] = useState<InvoiceCouponStorage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [statistics, setStatistics] = useState<CouponUsageStatistics | null>(null);
  
  // حالات البحث
  const [searchCriteria, setSearchCriteria] = useState<CouponStorageSearchCriteria>({
    status: 'active',
    skip: 0,
    limit: 10
  });
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  
  // حالات الفلاتر
  const [showFilters, setShowFilters] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  
  // تحميل البيانات
  const loadStorages = async (criteria: CouponStorageSearchCriteria = searchCriteria) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await couponStorageService.searchCouponStorages(criteria);
      
      if (result.success) {
        setStorages(result.data.storages);
        setTotalResults(result.data.total);
      } else {
        setError(result.message || 'حدث خطأ في البحث');
      }
    } catch (err) {
      setError('حدث خطأ أثناء تحميل البيانات');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // تحميل الإحصائيات
  const loadStatistics = async () => {
    try {
      const result = await couponStorageService.getCouponUsageStatistics();
      if (result.success && result.data) {
        setStatistics(result.data);
      }
    } catch (err) {
      console.error('خطأ في تحميل الإحصائيات:', err);
    }
  };

  // تأثير تحميل البيانات عند تحميل المكون
  useEffect(() => {
    loadStorages();
    loadStatistics();
  }, []);

  // البحث
  const handleSearch = () => {
    setCurrentPage(1);
    loadStorages({
      ...searchCriteria,
      skip: 0
    });
  };

  // إعادة تعيين الفلاتر
  const handleResetFilters = () => {
    setSearchCriteria({
      status: 'active',
      skip: 0,
      limit: 10
    });
    setCurrentPage(1);
    loadStorages({
      status: 'active',
      skip: 0,
      limit: 10
    });
  };

  // تغيير الصفحة
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    const newSkip = (page - 1) * (searchCriteria.limit || 10);
    loadStorages({
      ...searchCriteria,
      skip: newSkip
    });
  };

  // تغيير حالة السجل
  const handleStatusChange = async (invoiceId: string, newStatus: 'active' | 'cancelled') => {
    if (newStatus === 'cancelled') {
      if (!window.confirm('هل أنت متأكد من إلغاء سجل تخزين الكوبونات لهذه الفاتورة؟')) {
        return;
      }
    }

      try {
      const result = await couponStorageService.updateCouponStorage(invoiceId, {
        status: newStatus as 'active' | 'cancelled'
      });

      if (result.success) {
        setSuccess('تم تحديث حالة السجل بنجاح');
        loadStorages(searchCriteria);
        
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error || 'حدث خطأ أثناء تحديث الحالة');
      }
    } catch (err) {
      setError('حدث خطأ أثناء تحديث الحالة');
      console.error(err);
    }
  };

  // حذف سجل
  const handleDelete = async (invoiceId: string) => {
    if (!window.confirm('هل أنت متأكد من حذف سجل تخزين الكوبونات لهذه الفاتورة؟')) {
      return;
    }

    try {
      // ملاحظة: في النظام الحالي، نستخدم الإلغاء بدلاً من الحذف
      const result = await couponStorageService.cancelCouponStorage(invoiceId);

      if (result.success) {
        setSuccess('تم حذف سجل تخزين الكوبونات بنجاح');
        loadStorages(searchCriteria);
        
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error || 'حدث خطأ أثناء الحذف');
      }
    } catch (err) {
      setError('حدث خطأ أثناء الحذف');
      console.error(err);
    }
  };

  // تبديل توسيع/طي الصف
  const toggleRowExpansion = (invoiceId: string) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(invoiceId)) {
      newExpandedRows.delete(invoiceId);
    } else {
      newExpandedRows.add(invoiceId);
    }
    setExpandedRows(newExpandedRows);
  };

  // عرض التحميل
  if (loading && storages.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* رأس الصفحة */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={2} mb={2}>
            <Avatar
              sx={{
                bgcolor: theme.palette.primary.main,
                width: 40,
                height: 40,
              }}
            >
              <CouponIcon />
            </Avatar>
            <Box flex={1}>
              <Typography variant="h5" fontWeight={600}>
                إدارة تخزين أكواد الكوبونات
              </Typography>
              <Typography variant="body2" color="text.secondary">
                إدارة وتتبع جميع أكواد الكوبونات المطبقة على الفواتير
              </Typography>
            </Box>
          </Stack>

          {/* الإحصائيات السريعة */}
          {statistics && (
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CheckCircleIcon color="success" />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          الفواتير التي استخدمت كوبونات
                        </Typography>
                        <Typography variant="h5" fontWeight={600}>
                          {statistics.totalInvoicesWithCoupons}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </StatCard>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <TagIcon color="secondary" />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          إجمالي الكوبونات المطبقة
                        </Typography>
                        <Typography variant="h5" fontWeight={600}>
                          {statistics.totalCouponsApplied}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </StatCard>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <MoneyIcon color="error" />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          إجمالي قيمة الخصومات
                        </Typography>
                        <Typography variant="h5" fontWeight={600} color="error">
                          {statistics.totalDiscountAmount.toFixed(2)} ر.س
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </StatCard>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <AccountGroupIcon color="info" />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          أكثر الكوبونات استخداماً
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {statistics.topCoupons[0]?.code || '-'} ({statistics.topCoupons[0]?.usageCount || 0})
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </StatCard>
              </Grid>
            </Grid>
          )}

          {/* رسائل التنبيه */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {/* شريط البحث والفلاتر */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                <TextField
                  size="small"
                  placeholder="بحث برقم الفاتورة أو كود الكوبون..."
                  value={searchCriteria.invoiceNumber || ''}
                  onChange={(e) => setSearchCriteria({ ...searchCriteria, invoiceNumber: e.target.value })}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                  sx={{ flexGrow: 1 }}
                />
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                >
                  بحث
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  فلاتر
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={handleResetFilters}
                >
                  إعادة تعيين
                </Button>
              </Stack>

              <Collapse in={showFilters}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      size="small"
                      label="كود الكوبون"
                      fullWidth
                      value={searchCriteria.couponCode || ''}
                      onChange={(e) => setSearchCriteria({ ...searchCriteria, couponCode: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>الحالة</InputLabel>
                      <Select
                        value={searchCriteria.status || ''}
                        onChange={(e) => setSearchCriteria({ ...searchCriteria, status: e.target.value as 'active' | 'cancelled' })}
                        label="الحالة"
                      >
                        <MenuItem value="active">نشط</MenuItem>
                        <MenuItem value="cancelled">ملغي</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      size="small"
                      label="تاريخ من"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={searchCriteria.startDate || ''}
                      onChange={(e) => setSearchCriteria({ ...searchCriteria, startDate: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      size="small"
                      label="تاريخ إلى"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={searchCriteria.endDate || ''}
                      onChange={(e) => setSearchCriteria({ ...searchCriteria, endDate: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </Collapse>
            </CardContent>
          </Card>
        </CardContent>
      </Card>

      {/* جدول السجلات */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} mb={2}>
            سجلات تتبع الكوبونات ({totalResults})
          </Typography>
          
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>رقم الفاتورة</TableCell>
                  <TableCell>تاريخ الإنشاء</TableCell>
                  <TableCell>عدد الكوبونات</TableCell>
                  <TableCell>إجمالي الخصم</TableCell>
                  <TableCell>الحالة</TableCell>
                  <TableCell>الإجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {storages.map((storage) => {
                  const storageId = storage._id || storage.invoiceId;
                  return (
                    <React.Fragment key={storageId}>
                      <TableRow hover>
                        <TableCell>
                          <Typography fontWeight={600}>
                            {storage.invoiceNumber || storage.invoiceId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {format(new Date(storage.createdAt), 'dd MMM yyyy', { locale: arSA })}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={storage.coupons.length}
                            size="small"
                            color="primary"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography color="error" fontWeight={600}>
                            {storage.totalCouponDiscount.toFixed(2)} ر.س
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={storage.status === 'active' ? 'نشط' : 'ملغي'}
                            size="small"
                            color={storage.status === 'active' ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title={expandedRows.has(storageId) ? "طي التفاصيل" : "عرض التفاصيل"}>
                              <IconButton
                                size="small"
                                onClick={() => toggleRowExpansion(storageId)}
                              >
                                {expandedRows.has(storageId) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="حذف">
                               <IconButton
                                 size="small"
                                 onClick={() => handleDelete(storage.invoiceId)}
                               >
                                 <DeleteIcon color="error" />
                               </IconButton>
                             </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={6} sx={{ p: 0 }}>
                          <Collapse in={expandedRows.has(storageId)} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 3 }}>
                              <Typography variant="subtitle1" fontWeight={600} mb={2}>
                                تفاصيل الكوبونات ({storage.coupons.length})
                              </Typography>
                              <List>
                                {storage.coupons.map((coupon, index) => (
                                  <ListItem
                                    key={index}
                                    sx={{
                                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                                      borderRadius: 1,
                                      mb: 1,
                                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                                    }}
                                  >
                                    <ListItemIcon>
                                      <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 24, height: 24 }}>
                                        {coupon.discountType === 'percentage' ? <PercentIcon sx={{ fontSize: 16 }} /> : <MoneyIcon sx={{ fontSize: 16 }} />}
                                      </Avatar>
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                          <Typography fontWeight={600}>{coupon.code}</Typography>
                                          <Chip
                                            label={coupon.discountType === 'percentage' ? `${coupon.discountValue}%` : `${coupon.discountValue} ر.س`}
                                            size="small"
                                            color={coupon.discountType === 'percentage' ? 'primary' : 'secondary'}
                                          />
                                        </Stack>
                                      }
                                      secondary={
                                        <>
                                          <Typography variant="body2" color="text.secondary">
                                            {coupon.description || 'بدون وصف'}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            الخصم الإجمالي: {coupon.totalDiscount.toFixed(2)} ر.س | 
                                            تاريخ التطبيق: {format(new Date(coupon.appliedAt), 'dd MMM yyyy', { locale: arSA })}
                                          </Typography>
                                        </>
                                      }
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* التصفح */}
          {totalResults > (searchCriteria.limit || 10) && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={Math.ceil(totalResults / (searchCriteria.limit || 10))}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}

          {storages.length === 0 && !loading && (
            <Box textAlign="center" py={4}>
              <InfoIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography color="text.secondary">
                لا توجد سجلات تخزين للكوبونات
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CouponStoragePage;